import React, { useEffect, useMemo, useState } from 'react';
import { PageHeader } from '../ui/PageHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import { supabase } from '../lib/supabase';
import { timeAgo } from '../lib/timeAgo';

/**
 * REFERRALS — who is referring whom, how often, and who to recruit next (Evan 2026-10-16,
 * ahead of the campaign featuring the signup referral-name entry).
 *
 * One definer RPC (admin_referrals_overview, admin_referrals_v1.sql) reads
 * profiles.referred_by_user_id/_username — the live capture from setup-profile. The `referrals`
 * points table stays out of this page until that system activates (it is empty today).
 * "Activated" everywhere = has at least one live rating.
 */

type Referee = { username: string | null; joined: string; ratings: number; city: string | null };
type Referrer = {
  referrer_id: string;
  referrer: string | null;
  referrer_city: string | null;
  count: number;
  activated: number;
  referees: Referee[];
};
type Rec = { username: string; city: string | null; university: string | null; friends: number; ratings: number };
type Overview = {
  totals: { referred_signups: number; matched: number; unmatched: number; activated: number };
  referrers: Referrer[];
  unmatched_entries: { typed: string; by: string | null; joined: string }[];
  by_source: { source: string; n: number }[];
  most_connected: Rec[];
  power_low_friends: Rec[];
};

/** "Who referred who" columns (Evan 2026-11-04: "this should have columns that I can sort easily"). */
type SortKey = 'referrer' | 'city' | 'count' | 'activated' | 'rate';
const SORT_COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'referrer', label: 'Referrer', numeric: false },
  { key: 'city', label: 'City', numeric: false },
  { key: 'count', label: 'Referrals', numeric: true },
  { key: 'activated', label: 'Activated', numeric: true },
  { key: 'rate', label: 'Activation rate', numeric: true },
];
const activationRate = (r: Referrer) => (r.count > 0 ? r.activated / r.count : 0);

export function ReferralsScreen() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openReferrer, setOpenReferrer] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('count');
  const [sortDesc, setSortDesc] = useState(true);

  const sortedReferrers = useMemo(() => {
    const rows = [...(data?.referrers ?? [])];
    const textOf = (r: Referrer) => (sortKey === 'city' ? r.referrer_city : r.referrer) ?? '';
    const numOf = (r: Referrer) =>
      sortKey === 'rate' ? activationRate(r) : sortKey === 'activated' ? r.activated : r.count;
    rows.sort((a, b) => {
      if (sortKey === 'referrer' || sortKey === 'city') {
        // Blank cities stay at the bottom in both directions — they aren't a value to sort on.
        const ta = textOf(a);
        const tb = textOf(b);
        if (!ta !== !tb) return ta ? -1 : 1;
        const c = ta.localeCompare(tb);
        if (c !== 0) return sortDesc ? -c : c;
      } else {
        const c = numOf(a) - numOf(b);
        if (c !== 0) return sortDesc ? -c : c;
      }
      return (a.referrer ?? '').localeCompare(b.referrer ?? '');
    });
    return rows;
  }, [data, sortKey, sortDesc]);

  const toggleSort = (key: SortKey, numeric: boolean) => {
    if (key === sortKey) {
      setSortDesc((d) => !d);
      return;
    }
    setSortKey(key);
    setSortDesc(numeric); // Numbers start highest-first, names start A→Z.
  };

  useEffect(() => {
    void supabase.rpc('admin_referrals_overview').then(({ data: d, error: e }) => {
      if (e) {
        setError(
          /admin_referrals_overview/.test(e.message)
            ? 'Needs SQL applied: shado-admin/supabase/sql/admin_referrals_v1.sql'
            : e.message,
        );
        return;
      }
      setData(d as Overview);
    });
  }, []);

  return (
    <div className="admin-page mx-auto max-w-4xl">
      <PageHeader
        title="Referrals"
        subtitle="Who typed whose name at signup, how often it converts, and who to recruit for the campaign."
      />
      {error ? <div className="admin-banner-error mb-4">{error}</div> : null}
      {data === null && !error ? <SkeletonRows rows={8} /> : null}
      {data ? (
        <div className="space-y-6">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ['Referred signups', data.totals.referred_signups],
                ['Matched to a real user', data.totals.matched],
                ['Unmatched names', data.totals.unmatched],
                ['Activated (rated ≥1)', data.totals.activated],
              ] as const
            ).map(([label, n]) => (
              <div key={label} className="rounded-xl border border-border bg-surface px-4 py-2.5">
                <div className="font-display text-[20px] font-semibold tabular-nums text-white">{n}</div>
                <div className="text-[10.5px] uppercase tracking-[0.08em] text-mutedDim">{label}</div>
              </div>
            ))}
            {data.by_source.map((s) => (
              <div key={s.source} className="rounded-xl border border-border/60 bg-surface px-4 py-2.5">
                <div className="font-display text-[20px] font-semibold tabular-nums text-muted">{s.n}</div>
                <div className="text-[10.5px] uppercase tracking-[0.08em] text-mutedDim">src: {s.source}</div>
              </div>
            ))}
          </div>

          <section>
            <div className="admin-eyebrow mb-2">Who referred who</div>
            {data.referrers.length === 0 ? (
              <EmptyState compact title="No referred signups yet" hint="Names typed at signup land here the moment they match a user." />
            ) : (
              <div className="admin-card overflow-x-auto !p-0">
                <table className="w-full border-collapse text-left">
                  <thead>
                    <tr className="border-b border-border">
                      {SORT_COLUMNS.map((c) => {
                        const active = sortKey === c.key;
                        return (
                          <th
                            key={c.key}
                            scope="col"
                            aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : 'none'}
                            className="p-0 font-normal"
                          >
                            <button
                              type="button"
                              onClick={() => toggleSort(c.key, c.numeric)}
                              className={`flex w-full items-center gap-1 whitespace-nowrap px-4 py-2.5 text-[10.5px] uppercase tracking-[0.08em] hover:text-white ${
                                c.numeric ? 'justify-end' : ''
                              } ${active ? 'text-white' : 'text-mutedDim'}`}
                            >
                              {c.label}
                              <span aria-hidden className={active ? '' : 'opacity-30'}>
                                {active && !sortDesc ? '▴' : '▾'}
                              </span>
                            </button>
                          </th>
                        );
                      })}
                      <th scope="col" className="w-8 p-0">
                        <span className="sr-only">Expand</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedReferrers.map((r) => {
                      const open = openReferrer === r.referrer_id;
                      const toggle = () => setOpenReferrer(open ? null : r.referrer_id);
                      return (
                        <React.Fragment key={r.referrer_id}>
                          <tr
                            onClick={toggle}
                            className="cursor-pointer border-b border-border/60 last:border-b-0 hover:bg-white/[0.03]"
                          >
                            <td className="px-4 py-2.5 text-[13.5px] font-medium text-white">
                              <button
                                type="button"
                                aria-expanded={open}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggle();
                                }}
                                className="text-left"
                              >
                                @{r.referrer ?? '—'}
                              </button>
                            </td>
                            <td className="px-4 py-2.5 text-[12px] text-mutedDim">{r.referrer_city ?? '—'}</td>
                            <td className="px-4 py-2.5 text-right text-[13px] tabular-nums text-accent">{r.count}</td>
                            <td className="px-4 py-2.5 text-right text-[13px] tabular-nums text-emerald-300">{r.activated}</td>
                            <td className="px-4 py-2.5 text-right text-[13px] tabular-nums text-muted">
                              {Math.round(activationRate(r) * 100)}%
                            </td>
                            <td className="pr-4 text-right text-[11px] text-mutedDim">{open ? '▴' : '▾'}</td>
                          </tr>
                          {open ? (
                            <tr className="border-b border-border/60 last:border-b-0">
                              <td colSpan={6} className="space-y-1 px-4 py-3">
                                {r.referees.map((ref, i) => (
                                  <div key={`${ref.username}-${i}`} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                                    <span className="text-white">@{ref.username ?? '—'}</span>
                                    <span className="text-mutedDim">joined {timeAgo(ref.joined)}</span>
                                    <span className={ref.ratings > 0 ? 'text-emerald-300' : 'text-mutedDim'}>
                                      {ref.ratings} rating{ref.ratings === 1 ? '' : 's'}
                                    </span>
                                    {ref.city ? <span className="text-mutedDim">{ref.city}</span> : null}
                                  </div>
                                ))}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {data.unmatched_entries.length > 0 ? (
            <section>
              <div className="admin-eyebrow mb-2">Typed names that matched nobody</div>
              <div className="admin-card space-y-1">
                {data.unmatched_entries.map((u, i) => (
                  <div key={i} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">“{u.typed}”</span>
                    <span className="text-mutedDim">typed by @{u.by ?? '—'} · {timeAgo(u.joined)}</span>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-2">
            <div className="admin-card">
              <div className="admin-eyebrow mb-1">Campaign targets — most connected</div>
              <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                The most mutual friends on Shado — one post from them reaches the most users.
              </p>
              <div className="space-y-1.5">
                {data.most_connected.map((u) => (
                  <div key={u.username} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">@{u.username}</span>
                    <span className="tabular-nums text-sky-300">{u.friends} friends</span>
                    <span className="tabular-nums text-mutedDim">{u.ratings} ratings</span>
                    <span className="text-mutedDim">{u.university ?? u.city ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="admin-card">
              <div className="admin-eyebrow mb-1">Campaign targets — power users, few friends</div>
              <p className="mb-3 text-[11px] leading-relaxed text-mutedDim">
                5+ ratings but ≤2 friends — they love it alone; a referral nudge turns them into
                recruiters.
              </p>
              <div className="space-y-1.5">
                {data.power_low_friends.map((u) => (
                  <div key={u.username} className="flex flex-wrap items-baseline gap-x-3 text-[12px]">
                    <span className="text-white">@{u.username}</span>
                    <span className="tabular-nums text-[#b79cff]">{u.ratings} ratings</span>
                    <span className="tabular-nums text-mutedDim">{u.friends} friends</span>
                    <span className="text-mutedDim">{u.university ?? u.city ?? ''}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}
